<template>
  <form ref="formRef" class="-relative" method="post" action="/store/cart" novalidate>
    <csrf-input />
    <input type="hidden" name="itemType" :value="itemType" />
    <input type="hidden" name="addToCart" :value="itemId" />
    <input type="hidden" name="giftUserId" :value="giftUserId" />
    <input
      v-if="metaItemFromType"
      type="hidden"
      name="metaItemFromType"
      :value="metaItemFromType"
    />
    <input v-if="metaItemFromId" type="hidden" name="metaItemFromId" :value="metaItemFromId" />
    <icon-button
      type="submit"
      icon="shopping_bag"
      :icons-outlined="true"
      :reduce="false"
      :submitting="processing"
      submitting-label="Adding..."
      :classes="buttonClasses"
      :label="buttonLabel"
      aria-live="polite"
      @click-action="handleSubmit"
    />

    <!-- inline cart messages if redirecting to cart -->
    <timed-assertive-badge
      v-if="!serverRedirectToCart && !processing && success"
      message="Successfully added to cart!"
      @close="clearBadge"
    />
    <timed-assertive-badge
      v-if="!serverRedirectToCart && !processing && error"
      badge-type="error"
      :message="error"
      @close="clearBadge"
    />
  </form>
</template>

<script setup lang="ts">
import { nextTick, useTemplateRef, ref } from 'vue'
import CsrfInput from '@/components/forms/csrf-input.vue'
import IconButton from '@/components/buttons/icon-button.vue'
import { trackEvent as analyticsTrackEvent } from '@/utils/analytics'
import { fbApi } from '@/utils/http-api.ts'
import { useShopStore } from '@/stores/shop.ts'
import TimedAssertiveBadge from '@/components/badges/timed-assertive-badge.vue'

/** PROPS */
const props = withDefaults(
  defineProps<{
    itemType?: 'plan' | 'product'
    itemId: number
    giftUserId?: number
    metaItemFromType?: 'plan' | 'workout' | 'wellness_video' | 'article' | ''
    metaItemFromId?: number
    serverRedirectToCart?: boolean
    buttonClasses?: string
    buttonLabel?: string

    // used for GA add to cart event tracking
    trackEvent?: boolean
    eventCategory?: string
    eventAction?: string
    eventLabel?: string
  }>(),
  {
    itemType: 'plan',
    giftUserId: 0,
    metaItemFromType: '',
    metaItemFromId: 0,
    serverRedirectToCart: true,
    buttonClasses: '-ico-txt -no-hover -p-01',
    buttonLabel: 'Add To Bag',

    // used for GA add to cart event tracking
    trackEvent: false,
    eventCategory: '',
    eventAction: '',
    eventLabel: '',
  },
)

/** STATE */
const shopStore = useShopStore()
const processing = ref(false)
const success = ref(false)
const error = ref('')
const formRef = useTemplateRef('formRef')

/** METHODS */
function clearBadge() {
  success.value = false
  error.value = ''
}

async function handleSubmit() {
  if (processing.value) {
    return
  }
  // disable submit
  processing.value = true
  success.value = false
  error.value = ''

  try {
    // wrapper for GA event hitCallback so only invoked once
    let eventSubmitting = false
    const submitForm = async () => {
      if (eventSubmitting) {
        return
      }

      if (formRef.value) {
        // only allow once per cta, regardless of how many added to cart
        eventSubmitting = true

        // traditional form submit with redirect
        if (props.serverRedirectToCart) {
          formRef.value.submit()
          return
        }

        // fetch request
        try {
          const formData = new FormData(formRef.value)
          const postData = Object.fromEntries(formData.entries())

          // error modal will show on actual http error
          const res = await fbApi.post('/store/cart', {
            prefix: 'web',
            json: postData,
          })

          // shouldn't happen if API is up
          if (!res || !res.body || !res.body.data) {
            throw new Error(`Unable to add to cart, please refresh the page and try again`)
          }

          // not a full type
          const { cartItemCount }: { cartItemCount: number } = res.body.data

          // update cart count in nav
          shopStore.setCartItemCount(cartItemCount)
          success.value = true
        } catch (e) {
          // handle error
          console.error('Error adding to cart:', e)
          error.value = 'Error adding item to cart'
        } finally {
          processing.value = false
        }
        // end fetch request
      }
    }

    // ga event tracking
    if (props.trackEvent) {
      analyticsTrackEvent({
        category: props.eventCategory,
        action: props.eventAction,
        label: props.eventLabel,
        transport: 'beacon',

        // @ts-expect-error analytics.js is not a typescript file
        callback: submitForm,
      })

      // if hitCallback is not called for whatever reason, redirect w/o waiting
      setTimeout(submitForm, 1000)
    } else {
      // no tracking
      nextTick(submitForm)
    }
  } catch (e) {
    console.error(e)
    processing.value = false
  }
}
</script>
